import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { NavLink } from "react-router-dom";
import {
  FileText, Calendar, Megaphone, Plus, Facebook, Instagram, Clock, CheckCircle,
  FileEdit, Loader2, CalendarDays, Edit, Check, HelpCircle, Wifi, X, Sparkles,
  AlertCircle, Settings, ArrowRight, ShieldCheck, AlertTriangle, Euro, Gift,
  Users, ChevronRight, TrendingUp, RefreshCw, Play, Zap, ToggleLeft, ToggleRight,
  Activity, Timer
} from "lucide-react";
import PlatformIcon from '../components/PlatformIcon';
import OneClickPlanButton from '../components/OneClickPlanButton';
import { useTrialStatus } from "../hooks/usePlanSelection";
import { QA_UI_ENABLED } from "../config/features";

// ── AutopilotStatusBanner ─────────────────────────────────────────────────────
//
// Kommuniziert die Kern-Automatik: „Dein Monatsplan wird automatisch erstellt."
// + Pausieren/Reaktivieren. Zeigt sich nur bei aktivem Plan/Trial (sonst übernimmt
// TrialStatusBanner). Kein Pflicht-Klick mehr — das ist die Standard-Botschaft.
const AutopilotStatusBanner = () => {
  const [s, setS] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = () => axios.get(`${API}/autopilot/settings`).then(r => setS(r.data)).catch(() => setS(null));
  useEffect(() => { load(); }, []);

  if (!s || !s.plan_active) return null;  // nur für zahlende/Trial-Kunden

  const toggle = async (enabled) => {
    setBusy(true);
    try {
      await axios.post(`${API}/autopilot/toggle`, { enabled });
      await load();
    } catch { /* still */ } finally { setBusy(false); }
  };

  const running = s.auto_running;
  return (
    <div className={`rounded-2xl border p-5 flex items-start gap-4 ${
      running ? "border-emerald-200 bg-emerald-50" : "border-amber-200 bg-amber-50"
    }`}>
      <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${
        running ? "bg-emerald-100" : "bg-amber-100"
      }`}>
        {running
          ? <Zap size={20} className="text-emerald-600" />
          : <AlertCircle size={20} className="text-amber-600" />}
      </div>
      <div className="flex-1 min-w-0">
        {running ? (
          <>
            <h3 className="font-semibold text-emerald-900">Dein Monatsplan wird automatisch erstellt</h3>
            <p className="text-sm text-emerald-700 mt-1 leading-snug">
              Jeden Monat erstellt Förderfuchs deinen kompletten Plan automatisch. Fehlt der
              Plan für den laufenden Monat noch, kannst du ihn direkt darunter mit einem Klick
              sofort erzeugen — sobald er steht, verschwindet diese Schaltfläche wieder. Eigene
              Beiträge ergänzen oder Posts bearbeiten kannst du jederzeit.
            </p>
          </>
        ) : (
          <>
            <h3 className="font-semibold text-amber-900">Autopilot ist pausiert</h3>
            <p className="text-sm text-amber-700 mt-1 leading-snug">
              Solange pausiert, wird kein Monatsplan automatisch erstellt. Du kannst ihn
              jederzeit wieder aktivieren.
            </p>
          </>
        )}
      </div>
      <button
        onClick={() => toggle(!running)}
        disabled={busy}
        data-testid="autopilot-toggle"
        className={`flex-shrink-0 text-xs font-semibold px-4 py-2 rounded-lg transition-colors disabled:opacity-50 ${
          running
            ? "text-emerald-700 bg-emerald-100 hover:bg-emerald-200"
            : "text-white bg-amber-600 hover:bg-amber-700"
        }`}
      >
        {busy ? "…" : running ? "Pausieren" : "Aktivieren"}
      </button>
    </div>
  );
};

// ── PlanfinderBanner ──────────────────────────────────────────────────────────
//
// Erscheint auf dem Dashboard, wenn noch kein Plan explizit gewählt wurde.
// Verschwindet dauerhaft nach Dismiss (localStorage) oder sobald ein Plan gesetzt ist.
// Führt zum Planfinder-Wizard unter /planfinder.

const PLANFINDER_DISMISSED_KEY = "foerderfuchs_planfinder_banner_dismissed";
const PLAN_KEY = "foerderfuchs_current_plan";

const PlanfinderBanner = () => {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const hasPlan = localStorage.getItem(PLAN_KEY) !== null;
    const dismissed = localStorage.getItem(PLANFINDER_DISMISSED_KEY) === "1";
    setVisible(!hasPlan && !dismissed);
  }, []);

  const handleDismiss = () => {
    localStorage.setItem(PLANFINDER_DISMISSED_KEY, "1");
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <div className="relative rounded-2xl bg-gradient-to-br from-brand-indigo to-brand-indigo-light p-6 text-white shadow-lg shadow-indigo-200 overflow-hidden">
      {/* Dekorativer Hintergrund-Blob */}
      <div className="absolute -top-6 -right-6 w-32 h-32 bg-white/10 rounded-full pointer-events-none" />
      <div className="absolute -bottom-4 -left-4 w-20 h-20 bg-white/10 rounded-full pointer-events-none" />

      {/* Dismiss */}
      <button
        onClick={handleDismiss}
        aria-label="Banner schließen"
        className="absolute top-3 right-3 w-7 h-7 flex items-center justify-center rounded-full bg-white/20 hover:bg-white/30 transition-colors"
      >
        <X size={14} />
      </button>

      <div className="relative">
        <div className="flex items-center gap-2 mb-2">
          <Sparkles size={16} className="text-white/80" />
          <span className="text-xs font-semibold text-white/80 uppercase tracking-wider">
            Neu hier?
          </span>
        </div>
        <h2 className="font-heading text-lg font-bold mb-1">
          Finde den richtigen Plan in 2 Minuten
        </h2>
        <p className="text-sm text-white/85 leading-relaxed mb-4 max-w-md">
          Beantworte 2 kurze Fragen — wir zeigen dir, welches Paket am besten zu
          deinem Ziel passt. Dauert 2 Minuten.
        </p>
        <NavLink
          to="/planfinder"
          className="inline-flex items-center gap-2 px-4 py-2.5 bg-white text-brand-indigo rounded-xl font-semibold text-sm hover:bg-brand-indigo-tint transition-colors shadow-sm"
        >
          Planfinder starten →
        </NavLink>
      </div>
    </div>
  );
};

// ── TrialStatusBanner ─────────────────────────────────────────────────────────
//
// Erscheint wenn der Nutzer einen Plan hat (PLAN_KEY gesetzt).
// Zeigt Trial-Fortschritt in 3 Zuständen:
//   comfortable (≥4 Tage) → grünes Info-Band, per Session dismissbar
//   expiring    (1–3 Tage) → Amber-Warnung, nicht dismissbar
//   expired     (0 Tage)   → Rot-Fehler, nicht dismissbar
// Wenn kein Plan → bleibt leer (PlanfinderBanner übernimmt).

const TRIAL_BANNER_SESSION_KEY = "foerderfuchs_trial_banner_dismissed";

const TrialStatusBanner = () => {
  const { isActive, daysRemaining, daysElapsed } = useTrialStatus();
  const hasPlan = localStorage.getItem(PLAN_KEY) !== null;
  const [dismissed, setDismissed] = useState(
    () => sessionStorage.getItem(TRIAL_BANNER_SESSION_KEY) === "1"
  );

  // Serverautoritativer Abgleich: gekaufter Plan (oder Owner) → keine Trial-Banner,
  // egal was die lokale Trial-Uhr (localStorage) sagt. null = unbekannt → Bestand.
  const [serverPlan, setServerPlan] = useState(null);
  useEffect(() => {
    let active = true;
    axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/billing/plan`)
      .then((res) => { if (active) setServerPlan(res.data || null); })
      .catch(() => { /* fail-open: lokale Anzeige bleibt */ });
    return () => { active = false; };
  }, []);

  // Nur wenn Nutzer einen Plan hat (sonst übernimmt PlanfinderBanner)
  if (!hasPlan) return null;

  // Kauf abgeschlossen bzw. Owner-Workspace → Trial-Banner sind gegenstandslos
  if (serverPlan && (serverPlan.purchased || serverPlan.owner)) return null;

  // Kein Trial-Datum vorhanden (Edge-Case: Plan ohne selectPlan()-Aufruf gesetzt)
  if (daysRemaining === null) return null;

  // Abgelaufen — nicht dismissbar
  if (!isActive) {
    return (
      <div
        className="rounded-xl border border-red-200 bg-red-50 p-5 flex items-start gap-4"
        role="alert"
        data-testid="trial-banner-expired"
      >
        <AlertCircle size={20} className="text-red-500 flex-shrink-0 mt-0.5" />
        <div className="flex-1">
          <h3 className="font-semibold text-red-800">Deine Testphase ist abgelaufen</h3>
          <p className="text-sm text-red-700 mt-1">
            Wähle jetzt deinen Plan, um alle Funktionen unterbrechungsfrei weiterzunutzen.
          </p>
          <div className="flex flex-wrap items-center gap-3 mt-3">
            <NavLink
              to="/settings#plan-management"
              className="inline-flex items-center gap-1.5 text-sm font-semibold text-white bg-red-600 hover:bg-red-700 px-4 py-2 rounded-lg transition-colors"
            >
              <Settings size={14} />
              Plan aktivieren
            </NavLink>
            <NavLink
              to="/pricing"
              className="text-sm text-red-600 hover:text-red-700 font-medium"
            >
              Alle Pakete vergleichen →
            </NavLink>
          </div>
        </div>
      </div>
    );
  }

  // Läuft ab (≤3 Tage) — nicht dismissbar
  if (daysRemaining <= 3) {
    return (
      <div
        className="rounded-xl border border-amber-200 bg-amber-50 p-5 flex items-start gap-4"
        role="alert"
        data-testid="trial-banner-expiring"
      >
        <Clock size={20} className="text-amber-500 flex-shrink-0 mt-0.5" />
        <div className="flex-1">
          <h3 className="font-semibold text-amber-800">
            Noch {daysRemaining} {daysRemaining === 1 ? "Tag" : "Tage"} in der Testphase
          </h3>
          <p className="text-sm text-amber-700 mt-1">
            Deine kostenlose Testphase endet bald. Wechsle jetzt zum Vollzugang — keine Unterbrechung, keine verlorenen Daten.
          </p>
          <div className="flex flex-wrap items-center gap-3 mt-3">
            <NavLink
              to="/settings#plan-management"
              className="inline-flex items-center gap-1.5 text-sm font-semibold text-white bg-amber-600 hover:bg-amber-700 px-4 py-2 rounded-lg transition-colors"
            >
              <Settings size={14} />
              Plan jetzt wählen
            </NavLink>
            <NavLink
              to="/pricing"
              className="text-sm text-amber-700 hover:text-amber-800 font-medium"
            >
              Pakete vergleichen →
            </NavLink>
          </div>
        </div>
      </div>
    );
  }

  // Aktiv & genug Zeit (≥4 Tage) — per Session dismissbar
  if (dismissed) return null;

  const handleDismiss = () => {
    sessionStorage.setItem(TRIAL_BANNER_SESSION_KEY, "1");
    setDismissed(true);
  };

  return (
    <div
      className="relative rounded-xl border border-green-200 bg-green-50 p-5"
      role="note"
      data-testid="trial-banner-active"
    >
      <button
        onClick={handleDismiss}
        aria-label="Banner schließen"
        className="absolute top-3 right-3 w-7 h-7 flex items-center justify-center rounded-full bg-green-100 hover:bg-green-200 transition-colors"
      >
        <X size={14} className="text-green-700" />
      </button>

      <div className="flex items-start gap-4 pr-8">
        <CheckCircle size={20} className="text-green-500 flex-shrink-0 mt-0.5" />
        <div className="flex-1">
          <h3 className="font-semibold text-green-800">
            Testphase läuft — noch {daysRemaining} Tage kostenlos
          </h3>
          <p className="text-sm text-green-700 mt-1">
            Alle Funktionen sind freigeschaltet — veröffentlicht wird in der Testphase über Bluesky, weitere Kanäle folgen mit deinem Paket. Nach der Testphase wähle einfach deinen Tarif in den Einstellungen.
          </p>
          <NavLink
            to="/settings#plan-management"
            className="inline-flex items-center gap-1 text-sm text-green-700 hover:text-green-800 font-medium mt-2"
          >
            Plan vorab ansehen →
          </NavLink>
        </div>
      </div>
    </div>
  );
};

const ContentRow = ({ item }) => (
  <div className="flex items-center justify-between p-3 bg-stone-50 rounded-lg hover:bg-stone-100 transition-colors">
    <div className="flex-1 min-w-0">
      <p className="text-sm font-medium text-stone-800 truncate">
        {item.topic || item.title || item.hook || "Unbenannter Post"}
      </p>
      <p className="text-xs text-stone-400 mt-0.5">
        {item.platform || item.channel || "—"} · {item.status || "Entwurf"}
      </p>
    </div>
    <span className={`text-xs px-2 py-1 rounded-full ml-3 flex-shrink-0 ${
      item.status === "published" ? "bg-green-100 text-green-700" :
      item.status === "scheduled" ? "bg-blue-100 text-blue-700" :
      "bg-stone-200 text-stone-600"
    }`}>
      {item.status === "published" ? "Veröffentlicht" :
       item.status === "scheduled" ? "Geplant" : "Entwurf"}
    </span>
  </div>
);


const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const platformColors = {
  facebook: 'bg-blue-600 text-white',
  instagram: 'bg-gradient-to-r from-purple-500 to-pink-500 text-white',
  linkedin: 'bg-sky-700 text-white',
  tiktok: 'bg-gray-900 text-white',
  google_business: 'bg-orange-50 text-orange-600',
  youtube: 'bg-red-600 text-white',
  twitter: 'bg-sky-500 text-white',
};

const GoLiveChecklist = () => {
  const [items, setItems] = useState(null);

  useEffect(() => {
    const load = async () => {
      try {
        const [connectRes, planRes] = await Promise.all([
          // Einheitliche Status-API (social_connect) — liefert PlatformStatus-Enums
          axios.get(`${API}/social-connect/status`),
          // Serverautoritativer Plan-/Kanal-Zugriff (trial_active / purchased / channel_access)
          axios.get(`${API}/billing/plan`).catch(() => ({ data: null })),
        ]);
        const platforms = connectRes.data?.platforms || {};
        const plan = planRes.data;

        // Helper: "ready_to_post"/"connected" gilt als vollständig verbunden
        const isReady = (id) =>
          ["ready_to_post", "connected"].includes(platforms[id]?.status);

        // Ein verbundener Kanal (irgendeiner) genügt für den ersten Live-Post
        const anyConnected = Object.values(platforms).some((p) =>
          ["ready_to_post", "connected"].includes(p?.status));

        // Trial (kein Kauf, kein Owner) → Bluesky-First-Checkliste. Sonst die
        // klassische Mehr-Kanäle-Checkliste (gekaufte Kunden/Owner).
        const isTrial = plan && !plan.owner && plan.trial_active && !plan.purchased;

        if (isTrial) {
          setItems([
            {
              label: 'Bluesky verbunden',
              done: isReady('bluesky'),
              action: '/kanale',
              actionLabel: 'Verbinden',
            },
            {
              label: 'Erster Beitrag geplant',
              done: anyConnected,
              action: '/monthly-plan',
              actionLabel: 'Plan ansehen',
            },
            {
              label: 'Automatisch veröffentlichen aktiv',
              // plan.auto_publish kommt jetzt serverautoritativ aus /billing/plan.
              done: isReady('bluesky') && plan?.auto_publish === true,
              action: '/settings',
              actionLabel: 'Einstellen',
            },
          ]);
        } else {
          setItems([
            {
              label: 'Facebook & Instagram verbunden',
              done: isReady('facebook'),
              action: '/kanale',
              actionLabel: 'Verbinden',
            },
            {
              label: 'LinkedIn verbunden',
              done: isReady('linkedin'),
              action: '/kanale',
              actionLabel: 'Verbinden',
            },
            {
              label: 'Google Business verbunden',
              done: isReady('google_business'),
              action: '/kanale',
              actionLabel: 'Verbinden',
            },
            {
              label: 'Erster Beitrag geplant',
              done: anyConnected,
              action: '/monthly-plan',
              actionLabel: 'Plan ansehen',
            },
          ]);
        }
      } catch {
        /* Silently ignore – optional widget */
      }
    };
    load();
  }, []);

  if (!items) return null;

  const doneCount = items.filter(i => i.done).length;
  const allDone = doneCount === items.length;

  // ─── Herzlichen Glückwunsch Banner ─────────────────────────────────────
  if (allDone) {
    return (
      <div
        data-testid="congrats-banner"
        className="rounded-2xl bg-gradient-to-br from-green-500 via-emerald-500 to-teal-500 p-8 text-white shadow-xl shadow-green-200 relative overflow-hidden"
      >
        {/* Decorative blobs */}
        <div className="absolute -top-8 -right-8 w-40 h-40 bg-white/10 rounded-full" />
        <div className="absolute -bottom-6 -left-6 w-28 h-28 bg-white/10 rounded-full" />

        <div className="relative">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center">
              <CheckCircle size={28} className="text-white" />
            </div>
            <div>
              <p className="text-white/80 text-sm font-medium">Setup abgeschlossen</p>
              <h2 className="font-heading text-2xl font-bold">Herzlichen Glückwunsch!</h2>
            </div>
          </div>

          <p className="text-white/90 text-base leading-relaxed mb-6">
            Dein KI Social Media Manager ist startklar. Ab jetzt laufen deine Posts automatisch im Hintergrund – du musst nichts weiter tun.
          </p>

          <div className="flex flex-wrap gap-3">
            <NavLink
              to="/monthly-plan"
              data-testid="congrats-btn-plan"
              className="px-4 py-2.5 bg-white text-green-700 rounded-xl font-semibold text-sm hover:bg-green-50 transition-colors flex items-center gap-2"
            >
              <CalendarDays size={16} />
              Zum Monatsplan
            </NavLink>
            <NavLink
              to="/assistent"
              data-testid="congrats-btn-post"
              className="px-4 py-2.5 bg-white/20 text-white border border-white/30 rounded-xl font-semibold text-sm hover:bg-white/30 transition-colors flex items-center gap-2"
            >
              <Plus size={16} />
              Neuen Post erstellen
            </NavLink>
            <NavLink
              to="/hilfe"
              data-testid="congrats-btn-help"
              className="px-4 py-2.5 bg-white/20 text-white border border-white/30 rounded-xl font-semibold text-sm hover:bg-white/30 transition-colors flex items-center gap-2"
            >
              <HelpCircle size={16} />
              Hilfe & Best Practices
            </NavLink>
          </div>
        </div>
      </div>
    );
  }

  // ─── Normale Checkliste ─────────────────────────────────────────────────
  return (
    <div
      className="rounded-xl border border-stone-200 bg-white p-5"
      data-testid="go-live-checklist"
    >
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="font-semibold text-stone-800 flex items-center gap-2">
            <span className="w-5 h-5 rounded-full border-2 border-stone-300 flex items-center justify-center text-xs font-bold text-stone-500">
              {doneCount}
            </span>
            Go-Live Checkliste
          </h2>
          <p className="text-xs text-stone-500 mt-0.5">
            {doneCount} von {items.length} Schritten abgeschlossen
          </p>
        </div>
        <div className="flex gap-1">
          {items.map((item, idx) => (
            <div
              key={idx}
              className={`w-2 h-2 rounded-full ${item.done ? 'bg-green-500' : 'bg-stone-200'}`}
            />
          ))}
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        {items.map((item, idx) => (
          <div
            key={idx}
            className={`flex items-center gap-3 p-3 rounded-lg ${item.done ? 'bg-green-100/60' : 'bg-stone-50'}`}
          >
            <div className={`w-5 h-5 rounded-full flex-shrink-0 flex items-center justify-center ${item.done ? 'bg-green-500' : 'border-2 border-stone-300'}`}>
              {item.done && <Check size={12} className="text-white" />}
            </div>
            <span className={`text-sm flex-1 ${item.done ? 'text-green-800 line-through opacity-70' : 'text-stone-700'}`}>
              {item.label}
            </span>
            {!item.done && (
              <NavLink
                to={item.action}
                className="text-xs text-fox-fire font-medium hover:underline flex-shrink-0"
              >
                {item.actionLabel} →
              </NavLink>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

// ── Quick-Action-Tiles ────────────────────────────────────────────────────────
//
// 3 große Kacheln für die häufigsten Workflows — erscheinen auf dem Dashboard
// direkt nach der Go-Live-Checkliste.
const quickActions = [
  {
    to: '/assistent',
    icon: Sparkles,
    color: 'from-fox-fire to-orange-400',
    shadow: 'shadow-orange-200',
    title: 'Post schreiben',
    desc: 'KI-Assistent für schnelle Social-Media-Texte',
    cta: 'Jetzt starten',
  },
  {
    to: '/monthly-plan',
    icon: CalendarDays,
    color: 'from-sky-500 to-blue-500',
    shadow: 'shadow-blue-200',
    title: 'Monatsplan',
    desc: 'Alle geplanten Posts auf einen Blick',
    cta: 'Plan öffnen',
  },
  // Früher „Video erstellen" — Videos sind kein Kundenprodukt (nur Inhaber-Werkzeug).
  // Stattdessen der Schritt, der bei den meisten Kunden wirklich fehlt: Kanäle verbinden.
  {
    to: '/kanale',
    icon: Wifi,
    color: 'from-emerald-500 to-teal-500',
    shadow: 'shadow-emerald-200',
    title: 'Kanäle verbinden',
    desc: 'Instagram, Facebook & Co. anbinden — Voraussetzung fürs Veröffentlichen',
    cta: 'Kanäle öffnen',
  },
];

const QuickActionTiles = () => (
  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
    {quickActions.map((action) => {
      const Icon = action.icon;
      return (
        <NavLink
          key={action.to}
          to={action.to}
          className={`group relative bg-gradient-to-br ${action.color} rounded-2xl p-5 text-white shadow-lg ${action.shadow} overflow-hidden hover:scale-[1.02] transition-transform`}
        >
          {/* Decorative blob */}
          <div className="absolute -top-4 -right-4 w-20 h-20 bg-white/10 rounded-full pointer-events-none" />

          <div className="relative">
            <div className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center mb-3">
              <Icon size={20} className="text-white" />
            </div>
            <h3 className="font-bold text-base mb-1">{action.title}</h3>
            <p className="text-xs text-white/80 leading-relaxed mb-3">{action.desc}</p>
            <span className="inline-flex items-center gap-1 text-xs font-semibold text-white/90 group-hover:text-white transition-colors">
              {action.cta}
              <ArrowRight size={12} className="group-hover:translate-x-0.5 transition-transform" />
            </span>
          </div>
        </NavLink>
      );
    })}
  </div>
);

// ── Dashboard Widgets v2 — Status-Kacheln ──────────────────────────────────────
//
// Kompakte Informationskacheln, die beim ersten Blick den App-Zustand vermitteln:
// Posts diese Woche, Referral-Guthaben, Bonus-Status (+ Qualitäts-Kachel nur bei
// QA_UI_ENABLED, siehe unten). Datenquelle: GET /api/dashboard/summary (ein Request).

const PLATFORM_SHORT = {
  instagram: "Instagram",
  facebook:  "Facebook",
  linkedin:  "LinkedIn",
  tiktok:    "TikTok",
  google_business: "Google Business",
};

// Skeleton-Karte für den Lade-Zustand
const WidgetSkeleton = ({ className = "" }) => (
  <div className={`bg-white rounded-2xl border border-stone-200 p-5 animate-pulse space-y-3 ${className}`}>
    <div className="flex items-center gap-2">
      <div className="w-8 h-8 bg-stone-100 rounded-xl" />
      <div className="h-3.5 w-28 bg-stone-100 rounded" />
    </div>
    <div className="h-8 w-16 bg-stone-100 rounded" />
    <div className="h-3 w-40 bg-stone-100 rounded" />
    <div className="h-8 w-28 bg-stone-100 rounded-xl" />
  </div>
);

// ── Widget 1: Posts diese Woche ───────────────────────────────────────────────
const WeekPostsWidget = ({ data }) => {
  const { posts_this_week = 0, next_post = null } = data;

  const formatDate = (dateStr) => {
    if (!dateStr) return "—";
    const d = new Date(dateStr + "T00:00:00");
    return d.toLocaleDateString("de-DE", { weekday: "short", day: "numeric", month: "short" });
  };

  return (
    <div className="bg-white rounded-2xl border border-stone-200 p-5 flex flex-col gap-3 hover:shadow-sm transition-shadow group">
      <div className="flex items-center gap-2">
        <div className="w-8 h-8 bg-sky-50 rounded-xl flex items-center justify-center">
          <CalendarDays size={16} className="text-sky-500" />
        </div>
        <span className="text-xs font-semibold text-stone-500 uppercase tracking-wide">
          Diese Woche
        </span>
      </div>

      <div>
        <p className="text-3xl font-bold text-stone-900">{posts_this_week}</p>
        <p className="text-sm text-stone-500">
          geplante {posts_this_week === 1 ? "Post" : "Posts"}
        </p>
      </div>

      {next_post ? (
        <div className="bg-stone-50 rounded-xl px-3 py-2 text-xs text-stone-600 leading-snug">
          <span className="font-semibold text-stone-700">Nächster Post:</span>{" "}
          {formatDate(next_post.date)} · {PLATFORM_SHORT[next_post.platform] ?? next_post.platform}
          {next_post.hook && (
            <p className="text-stone-400 mt-0.5 truncate">„{next_post.hook}"</p>
          )}
        </div>
      ) : (
        <p className="text-xs text-stone-400 italic">Kein Post in dieser Woche geplant.</p>
      )}

      <NavLink
        to="/monthly-plan"
        className="mt-auto flex items-center gap-1.5 text-xs font-semibold text-sky-600 hover:text-sky-700 transition-colors"
      >
        Kalender öffnen <ChevronRight size={13} />
      </NavLink>
    </div>
  );
};

// ── Widget 2: Qualitäts-Check (nur bei QA_UI_ENABLED) ─────────────────────────
//
// Die Qualitätsprüfung ist ab sofort interne Systemarbeit — sie läuft automatisch
// beim Erzeugen der Posts. Diese Kachel machte daraus eine Kundenaufgabe („Klick auf
// Monat prüfen"). Wieder einblenden: REACT_APP_QA_UI_ENABLED=true + Rebuild.
const QualityWidget = ({ data }) => {
  const { needs_attention = 0, total_checked = 0 } = data.quality ?? {};
  const allGood = total_checked > 0 && needs_attention === 0;

  return (
    <div className="bg-white rounded-2xl border border-stone-200 p-5 flex flex-col gap-3 hover:shadow-sm transition-shadow">
      <div className="flex items-center gap-2">
        <div className={`w-8 h-8 rounded-xl flex items-center justify-center ${
          allGood ? "bg-green-50" : needs_attention > 0 ? "bg-red-50" : "bg-stone-50"
        }`}>
          {allGood
            ? <ShieldCheck size={16} className="text-green-500" />
            : needs_attention > 0
              ? <AlertTriangle size={16} className="text-red-400" />
              : <ShieldCheck size={16} className="text-stone-400" />
          }
        </div>
        <span className="text-xs font-semibold text-stone-500 uppercase tracking-wide">
          Qualitäts-Check
        </span>
      </div>

      <div>
        {allGood ? (
          <>
            <p className="text-lg font-bold text-green-700">Alles gut ✓</p>
            <p className="text-sm text-stone-500">
              {total_checked} Post{total_checked !== 1 ? "s" : ""} geprüft — keine Hinweise.
            </p>
          </>
        ) : needs_attention > 0 ? (
          <>
            <p className="text-3xl font-bold text-red-600">{needs_attention}</p>
            <p className="text-sm text-stone-500">
              {needs_attention === 1 ? "Post braucht" : "Posts brauchen"} Feinschliff
            </p>
          </>
        ) : (
          <>
            <p className="text-lg font-bold text-stone-500">Noch nicht geprüft</p>
            <p className="text-sm text-stone-400">Klick auf „Monat prüfen" im Kalender.</p>
          </>
        )}
      </div>

      {total_checked > 0 && !allGood && needs_attention > 0 && (
        <div className="flex items-center gap-1.5 text-[11px] text-stone-400">
          <TrendingUp size={10} className="text-green-500" />
          {total_checked - needs_attention} von {total_checked} Posts sehen gut aus.
        </div>
      )}

      <NavLink
        to="/monthly-plan"
        className="mt-auto flex items-center gap-1.5 text-xs font-semibold text-violet-600 hover:text-violet-700 transition-colors"
      >
        {needs_attention > 0 ? "Jetzt verbessern" : "Monat prüfen"} <ChevronRight size={13} />
      </NavLink>
    </div>
  );
};

// ── Widget 3: Referral-Guthaben ───────────────────────────────────────────────
const ReferralWidget = ({ data }) => {
  const { credit_euros = 0, total_referrals = 0, paid_referrals = 0 } = data.referral ?? {};

  return (
    <div className="bg-white rounded-2xl border border-stone-200 p-5 flex flex-col gap-3 hover:shadow-sm transition-shadow">
      <div className="flex items-center gap-2">
        <div className="w-8 h-8 bg-orange-50 rounded-xl flex items-center justify-center">
          <Users size={16} className="text-orange-500" />
        </div>
        <span className="text-xs font-semibold text-stone-500 uppercase tracking-wide">
          Freunde werben
        </span>
      </div>

      <div>
        <p className="text-3xl font-bold text-stone-900">
          {credit_euros > 0
            ? `${credit_euros.toFixed(2)} €`
            : "0,00 €"
          }
        </p>
        <p className="text-sm text-stone-500">aktuelles Guthaben</p>
      </div>

      <div className="flex items-center gap-3 text-xs text-stone-500">
        <div className="flex items-center gap-1">
          <Users size={11} className="text-stone-400" />
          <span>{total_referrals} Einladung{total_referrals !== 1 ? "en" : ""}</span>
        </div>
        {paid_referrals > 0 && (
          <div className="flex items-center gap-1 text-green-600">
            <CheckCircle size={10} />
            <span>{paid_referrals} erfolgreich</span>
          </div>
        )}
      </div>

      {credit_euros === 0 && (
        <p className="text-xs text-stone-400 leading-snug">
          Lade Freunde ein — für jede erfolgreiche Empfehlung erhältst du Guthaben.
        </p>
      )}

      <NavLink
        to="/bonus"
        className="mt-auto flex items-center gap-1.5 text-xs font-semibold text-orange-600 hover:text-orange-700 transition-colors"
      >
        Zum Bonus-Center <ChevronRight size={13} />
      </NavLink>
    </div>
  );
};

// ── Widget 4: Förderfuchs-Bonus ───────────────────────────────────────────────
const BonusWidget = ({ data }) => {
  const { enabled = false, bonus_posts_this_month = 0 } = data.foerderfuchs_bonus ?? {};

  return (
    // Ohne die Qualitäts-Kachel bleiben 3 Kacheln im 2-Spalten-Raster übrig — die
    // letzte füllt dann beide Spalten, damit keine leere Lücke entsteht.
    <div className={`rounded-2xl border p-5 flex flex-col gap-3 hover:shadow-sm transition-shadow ${
      QA_UI_ENABLED ? "" : "sm:col-span-2"
    } ${
      enabled ? "bg-violet-50 border-violet-200" : "bg-white border-stone-200"
    }`}>
      <div className="flex items-center gap-2">
        <div className={`w-8 h-8 rounded-xl flex items-center justify-center ${
          enabled ? "bg-violet-100" : "bg-stone-50"
        }`}>
          <Gift size={16} className={enabled ? "text-violet-500" : "text-stone-400"} />
        </div>
        <span className="text-xs font-semibold text-stone-500 uppercase tracking-wide">
          Förderfuchs-Bonus
        </span>
        {enabled && (
          <span className="ml-auto text-[10px] font-bold px-2 py-0.5 bg-violet-200 text-violet-800 rounded-full">
            AKTIV
          </span>
        )}
      </div>

      {enabled ? (
        <div>
          <p className="text-3xl font-bold text-violet-700">{bonus_posts_this_month}</p>
          <p className="text-sm text-violet-600">
            Bonus-Post{bonus_posts_this_month !== 1 ? "s" : ""} diesen Monat
          </p>
        </div>
      ) : (
        <div>
          <p className="text-lg font-bold text-stone-600">5 Extra-Posts</p>
          <p className="text-sm text-stone-500">noch nicht aktiviert</p>
        </div>
      )}

      <p className="text-xs leading-relaxed text-stone-500">
        {enabled
          ? "Bis zu 5 Posts/Monat werden automatisch im Kalender eingeplant — max. 1 Markenerwähnung/Woche."
          : "Aktiviere den Bonus und erhalte 5 Extra-Posts pro Monat — gegen max. 1 Markenerwähnung/Woche."
        }
      </p>

      <NavLink
        to="/bonus"
        className={`mt-auto flex items-center gap-1.5 text-xs font-semibold transition-colors ${
          enabled ? "text-violet-600 hover:text-violet-700" : "text-stone-500 hover:text-stone-700"
        }`}
      >
        {enabled ? "Bonus verwalten" : "Bonus freischalten"}
        <ChevronRight size={13} />
      </NavLink>
    </div>
  );
};

// ── Widget 5: Auto-Scheduler ──────────────────────────────────────────────────
const SchedulerWidget = ({ data, onToggle, onRunNow, toggling, running }) => {
  const sched = data.scheduler ?? {};
  const {
    enabled = false,
    interval_minutes = 5,
    next_run = null,
    pending_posts = 0,
    last_run = null,
  } = sched;

  const formatTs = (ts) => {
    if (!ts) return "—";
    return new Date(ts).toLocaleString("de-DE", {
      day: "2-digit", month: "2-digit",
      hour: "2-digit", minute: "2-digit",
    });
  };

  const hasFailures = last_run?.failed > 0;

  return (
    <div className={`rounded-2xl border p-5 flex flex-col gap-3 hover:shadow-sm transition-shadow sm:col-span-2 ${
      enabled ? "bg-green-50 border-green-200" : "bg-white border-stone-200"
    }`}>
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className={`w-8 h-8 rounded-xl flex items-center justify-center ${
            enabled ? "bg-green-100" : "bg-stone-50"
          }`}>
            <Activity size={16} className={enabled ? "text-green-600" : "text-stone-400"} />
          </div>
          <div>
            <div className="flex items-center gap-2">
              <span className="text-xs font-semibold text-stone-500 uppercase tracking-wide">
                Auto-Scheduler
              </span>
              {enabled && (
                <span className="text-[10px] font-bold px-2 py-0.5 bg-green-200 text-green-800 rounded-full">
                  AKTIV
                </span>
              )}
            </div>
            <p className="text-[10px] text-stone-400 mt-0.5">
              {enabled
                ? `Prüft alle ${interval_minutes} Min. auf fällige Beiträge`
                : "Veröffentlicht geplante Beiträge automatisch"}
            </p>
          </div>
        </div>

        {/* Toggle */}
        <button
          onClick={onToggle}
          disabled={toggling}
          className={`flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-semibold transition-colors ${
            toggling ? "opacity-40 cursor-not-allowed bg-stone-100 text-stone-400" :
            enabled
              ? "bg-stone-100 text-stone-600 hover:bg-stone-200"
              : "bg-green-600 text-white hover:bg-green-700"
          }`}
          title={
            enabled
              ? "Scheduler pausieren — geplante Posts werden nicht mehr automatisch veröffentlicht"
              : "Scheduler starten — fällige Posts werden alle " + interval_minutes + " Minuten automatisch veröffentlicht"
          }
        >
          {toggling ? <Loader2 size={12} className="animate-spin" /> :
           enabled ? <ToggleRight size={14} /> : <ToggleLeft size={14} />}
          {enabled ? "Deaktivieren" : "Aktivieren"}
        </button>
      </div>

      {/* Status-Grid */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
        {/* Status */}
        <div className="bg-white/80 rounded-xl p-3 text-center">
          <div className={`w-2.5 h-2.5 rounded-full mx-auto mb-1 ${
            enabled ? "bg-green-500 animate-pulse" : "bg-stone-300"
          }`} />
          <p className="text-[11px] text-stone-500 font-medium">Status</p>
          <p className={`text-xs font-bold ${enabled ? "text-green-700" : "text-stone-500"}`}>
            {enabled ? "Läuft" : "Gestoppt"}
          </p>
        </div>

        {/* Ausstehend */}
        <div className="bg-white/80 rounded-xl p-3 text-center">
          <p className="text-lg font-bold text-stone-800">{pending_posts}</p>
          <p className="text-[11px] text-stone-500 font-medium">Ausstehend</p>
        </div>

        {/* Letzter Run */}
        <div className="bg-white/80 rounded-xl p-3 text-center">
          {last_run ? (
            <>
              <p className="text-[11px] text-stone-500 font-medium">Letzter Run</p>
              <p className="text-xs font-semibold text-stone-700">{formatTs(last_run.timestamp)}</p>
              <div className="flex items-center justify-center gap-2 mt-0.5">
                <span className="text-[10px] text-green-600 font-medium">✓ {last_run.published}</span>
                {last_run.failed > 0 && (
                  <span className="text-[10px] text-red-500 font-medium">✗ {last_run.failed}</span>
                )}
              </div>
            </>
          ) : (
            <>
              <p className="text-[11px] text-stone-500 font-medium">Letzter Run</p>
              <p className="text-xs text-stone-400">Noch kein Run</p>
            </>
          )}
        </div>

        {/* Nächster Run */}
        <div className="bg-white/80 rounded-xl p-3 text-center">
          <p className="text-[11px] text-stone-500 font-medium">Nächster Run</p>
          {enabled && next_run ? (
            <p className="text-xs font-semibold text-stone-700">{formatTs(next_run)}</p>
          ) : (
            <p className="text-xs text-stone-400">
              {enabled ? `alle ${interval_minutes} Min.` : "—"}
            </p>
          )}
        </div>
      </div>

      {/* Fehler-Warnung */}
      {hasFailures && (
        <div className="flex items-start gap-2 bg-red-50 border border-red-200 rounded-xl px-3 py-2 text-xs text-red-700">
          <AlertTriangle size={12} className="flex-shrink-0 mt-0.5" />
          <div>
            <span className="font-medium">
              Letzter Run: {last_run.failed} fehlgeschlagene{last_run.failed !== 1 ? " Posts" : " Post"}.
            </span>
            <span className="block text-red-500 mt-0.5">
              Plattform-Verbindung unter{" "}
              <a href="/kanale" className="underline font-medium">Kanäle</a>{" "}
              prüfen oder Backend-Logs kontrollieren.
            </span>
          </div>
        </div>
      )}

      {/* Aktions-Reihe */}
      <div className="flex items-center gap-2 pt-1">
        <button
          onClick={onRunNow}
          disabled={running || !enabled}
          className={`flex items-center gap-1.5 px-4 py-2 rounded-xl text-xs font-semibold transition-colors ${
            running || !enabled
              ? "bg-stone-100 text-stone-400 cursor-not-allowed"
              : "bg-green-600 text-white hover:bg-green-700"
          }`}
          title={
            !enabled
              ? "Scheduler ist deaktiviert — erst aktivieren, dann ausführen"
              : pending_posts > 0
                ? `${pending_posts} fällige${pending_posts !== 1 ? " Posts" : " Post"} sofort veröffentlichen`
                : "Scheduler einmal sofort durchlaufen lassen (auch wenn keine Posts fällig)"
          }
        >
          {running ? <Loader2 size={12} className="animate-spin" /> : <Play size={12} />}
          {running ? "Läuft…" : "Jetzt ausführen"}
        </button>

        <NavLink
          to="/settings"
          className="flex items-center gap-1.5 px-4 py-2 rounded-xl text-xs font-semibold text-stone-500 hover:text-stone-700 hover:bg-stone-100 transition-colors"
        >
          <Settings size={12} />
          Einstellungen
        </NavLink>

        {pending_posts > 0 && !running && (
          <span className="ml-auto text-xs text-amber-600 font-medium bg-amber-50 border border-amber-200 px-2.5 py-1 rounded-xl">
            {pending_posts} fällig
          </span>
        )}
      </div>
    </div>
  );
};

// ── Widget 6: Systemzustand ───────────────────────────────────────────────────
//
// Kompakte Status-Zeile am Ende der Widget-Reihe — NUR für den Inhaber/Master.
// Die Hinweise sind Betriebs-Interna mit Dev-Jargon ("CLIENT_ID/SECRET in .env
// fehlen") und haben auf einem Kunden-Dashboard nichts verloren. Bewusst
// fail-closed (nur bei bestätigtem Master), anders als die Inhaber-Werkzeuge:
// lieber dem Inhaber eine Statuszeile vorenthalten als Kunden Interna zeigen.
// Lädt /api/systemcheck einmalig im Hintergrund — kein Blocking.
// Zeigt bei allem OK eine grüne Zeile; bei Hinweisen max. 2 inline-Chips.
const SystemStatusBar = () => {
  const [hints, setHints] = useState(null); // null = loading, [] = ok, [...] = hints
  const [isMaster, setIsMaster] = useState(false);

  useEffect(() => {
    axios.get(`${API}/account/me`)
      .then(res => setIsMaster(res.data?.is_master === true))
      .catch(() => setIsMaster(false));
    axios.get(`${API}/systemcheck`)
      .then(res => setHints(res.data?.summary?.hints ?? []))
      .catch(() => setHints(null)); // Fehler → keine Anzeige
  }, []);

  // Kunde, noch ladend oder Backend offline → kein Render
  if (!isMaster || hints === null) return null;

  const ok = hints.length === 0;

  return (
    <div className={`sm:col-span-2 rounded-xl border px-4 py-3 flex items-center gap-3 flex-wrap ${
      ok ? "border-green-200 bg-green-50" : "border-amber-200 bg-amber-50"
    }`}>
      <div className={`flex items-center gap-1.5 text-xs font-semibold flex-shrink-0 ${
        ok ? "text-green-700" : "text-amber-700"
      }`}>
        {ok
          ? <><span className="w-1.5 h-1.5 rounded-full bg-green-500" />System: OK</>
          : <><AlertTriangle size={12} />Systemhinweise</>
        }
      </div>
      {hints.slice(0, 2).map((h, i) => (
        <span
          key={i}
          className="text-[11px] px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 border border-amber-200"
        >
          {h}
        </span>
      ))}
      {hints.length > 2 && (
        <span className="text-[11px] text-amber-600">+{hints.length - 2} weitere</span>
      )}
    </div>
  );
};

// ── DashboardWidgets — Haupt-Container ───────────────────────────────────────
const DashboardWidgets = () => {
  const [summary, setSummary]     = useState(null);
  const [loading, setLoading]     = useState(true);
  const [error, setError]         = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [toggling, setToggling]   = useState(false);
  const [running, setRunning]     = useState(false);
  // Der globale Auto-Scheduler-Schalter ist ein Betreiber-Werkzeug (installationsweit).
  // Kunden dürfen ihn nicht schalten (Backend 403) → Widget nur für den Inhaber zeigen;
  // die Kunden-Automatik läuft standardmäßig (auto_publish) und wird in den Einstellungen
  // pausiert.
  const [isMaster, setIsMaster] = useState(false);
  useEffect(() => {
    axios.get(`${API}/account/me`)
      .then(res => setIsMaster(res.data?.is_master === true))
      .catch(() => setIsMaster(false));
  }, []);

  const load = async (silent = false) => {
    if (!silent) setLoading(true);
    else setRefreshing(true);
    setError(false);
    try {
      const res = await axios.get(`${API}/dashboard/summary`);
      setSummary(res.data);
    } catch (_) {
      setError(true);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => { load(); }, []);

  const handleToggleScheduler = async () => {
    if (!summary || toggling) return;
    const currentlyEnabled = summary.scheduler?.enabled ?? false;
    const nextEnabled = !currentlyEnabled;
    setToggling(true);
    try {
      await axios.post(`${API}/auto-scheduler/toggle`, { enabled: nextEnabled });
      toast.success(nextEnabled ? "Auto-Scheduler aktiviert" : "Auto-Scheduler deaktiviert");
      // Summary neu laden
      await load(true);
    } catch (_) {
      toast.error("Auto-Scheduler konnte nicht umgeschaltet werden.");
    } finally {
      setToggling(false);
    }
  };

  const handleRunNow = async () => {
    if (running) return;
    setRunning(true);
    try {
      toast.info("Starte Durchlauf…");
      const res = await axios.post(`${API}/auto-scheduler/run-now`);
      const pub = res.data.published_count ?? 0;
      const fail = res.data.failed_count ?? 0;
      if (pub > 0) {
        toast.success(`✅ ${pub} Post${pub !== 1 ? "s" : ""} veröffentlicht${fail > 0 ? ` · ${fail} fehlgeschlagen` : ""}`);
      } else if (fail > 0) {
        toast.error(`${fail} Post${fail !== 1 ? "s" : ""} fehlgeschlagen`);
      } else {
        toast.info("Kein fälliger Post gefunden.");
      }
      await load(true);
    } catch (_) {
      toast.error("Manueller Durchlauf fehlgeschlagen.");
    } finally {
      setRunning(false);
    }
  };

  return (
    <div className="space-y-3">
      {/* Sektion-Header */}
      <div className="flex items-center justify-between">
        <h2 className="font-heading font-semibold text-stone-700 text-sm">Dein Überblick</h2>
        <button
          onClick={() => load(true)}
          disabled={loading || refreshing}
          className="flex items-center gap-1 text-xs text-stone-400 hover:text-stone-600 transition-colors disabled:opacity-40"
          title="Aktualisieren"
        >
          <RefreshCw size={12} className={refreshing ? "animate-spin" : ""} />
          {refreshing ? "Lädt…" : "Aktualisieren"}
        </button>
      </div>

      {/* Fehler-Zustand */}
      {error && !loading && (
        <div className="text-xs text-stone-400 bg-stone-50 border border-stone-200 rounded-xl px-4 py-3">
          Dashboard-Daten konnten gerade nicht geladen werden.{" "}
          <button onClick={() => load()} className="text-fox-fire hover:underline font-medium">
            Erneut versuchen
          </button>
        </div>
      )}

      {/* Widget-Raster: 2 Spalten Desktop, 1 Spalte Mobil */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {loading ? (
          <>
            <WidgetSkeleton />
            <WidgetSkeleton />
            {QA_UI_ENABLED && <WidgetSkeleton />}
            <WidgetSkeleton className={QA_UI_ENABLED ? "" : "sm:col-span-2"} />
            {/* Scheduler-Skeleton: 2-spaltig */}
            <div className="sm:col-span-2 bg-white rounded-2xl border border-stone-200 p-5 animate-pulse space-y-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 bg-stone-100 rounded-xl" />
                  <div className="h-3.5 w-32 bg-stone-100 rounded" />
                </div>
                <div className="h-8 w-24 bg-stone-100 rounded-xl" />
              </div>
              <div className="grid grid-cols-4 gap-2">
                {[0,1,2,3].map(i => <div key={i} className="h-16 bg-stone-100 rounded-xl" />)}
              </div>
            </div>
          </>
        ) : summary ? (
          <>
            <WeekPostsWidget  data={summary} />
            {/* Qualitäts-Kachel: interne Systemarbeit, für Kunden ausgeblendet (QA_UI_ENABLED) */}
            {QA_UI_ENABLED && <QualityWidget data={summary} />}
            <ReferralWidget   data={summary} />
            <BonusWidget      data={summary} />
            {/* Scheduler — volle Breite (2 Spalten). Nur Betreiber: der globale
                Veröffentlichungs-Dienst ist installationsweit, nicht pro Kunde. */}
            {isMaster && (
              <SchedulerWidget
                data={summary}
                onToggle={handleToggleScheduler}
                onRunNow={handleRunNow}
                toggling={toggling}
                running={running}
              />
            )}
            {/* Systemzustand — volle Breite (2 Spalten), nach Scheduler */}
            <SystemStatusBar />
          </>
        ) : null}
      </div>
    </div>
  );
};

// Letzte Aktivitäten – Dashboard Widget
const RecentActivitiesBox = () => {
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/auto-scheduler/recent-activities`)
      .then(res => setActivities(res.data?.activities || []))
      .catch(() => {/* silent */})
      .finally(() => setLoading(false));
  }, []);

  const activityPlatformColors = {
    facebook: 'bg-blue-600',
    instagram: 'bg-gradient-to-br from-purple-500 to-pink-500',
    linkedin: 'bg-sky-700',
    tiktok: 'bg-gray-900',
    google_business: 'bg-amber-500',
  };

  const platformLabel = {
    facebook: 'FB',
    instagram: 'IG',
    linkedin: 'in',
    tiktok: 'TT',
    google_business: 'G',
  };

  if (loading || activities.length === 0) return null;

  return (
    <div className="bg-white rounded-xl border border-stone-200 p-5" data-testid="recent-activities-box">
      <h2 className="font-semibold text-stone-800 mb-3 flex items-center gap-2 text-sm">
        <Clock size={16} className="text-stone-400" />
        Letzte Aktivitäten
      </h2>
      <div className="space-y-2">
        {activities.slice(0, 5).map((act, idx) => (
          <div key={idx} className="flex items-center gap-3 p-2.5 rounded-lg bg-stone-50 hover:bg-stone-100 transition-colors">
            {/* Platform Icon */}
            <div className={`w-8 h-8 rounded-md flex items-center justify-center text-white text-xs font-bold flex-shrink-0 ${activityPlatformColors[act.platform] || 'bg-stone-400'}`}>
              {platformLabel[act.platform] || act.platform.substring(0, 2).toUpperCase()}
            </div>
            {/* Content */}
            <div className="flex-1 min-w-0">
              <p className="text-sm text-stone-700 truncate">{act.hook || 'Post'}</p>
              <p className="text-xs text-stone-400">
                {act.timestamp
                  ? new Date(act.timestamp).toLocaleString('de-DE', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })
                  : '—'}
              </p>
            </div>
            {/* Status Badge */}
            <span className={`text-xs px-2 py-0.5 rounded-full font-medium flex-shrink-0 ${
              act.status === 'published'
                ? 'bg-green-100 text-green-700'
                : 'bg-red-100 text-red-600'
            }`}>
              {act.status === 'published' ? '✓ Gepostet' : '✗ Fehler'}
            </span>
          </div>
        ))}
      </div>
      {activities.length > 5 && (
        <p className="text-xs text-stone-400 mt-2 text-right">
          +{activities.length - 5} weitere im{' '}
          <NavLink to="/monthly-plan" className="text-fox-fire hover:underline">Monatsplan</NavLink>
        </p>
      )}
    </div>
  );
};

// Dashboard Page
const Dashboard = () => {
  const [stats, setStats] = useState(null);
  const [recentContent, setRecentContent] = useState([]);
  const [loading, setLoading] = useState(true);
  // Seit die Kacheln echte Zahlen aus monthly_plan zeigen (Fund 24.08.2026: vorher
  // zählten sie dauerhaft 0 in einer nicht existierenden Collection), muss ein
  // Ladefehler sichtbar bleiben — sonst läse ein Kunde mit echten Entwürfen "0
  // Content-Entwürfe" und würde das für ein leeres Konto statt für einen
  // fehlgeschlagenen Abruf halten. Derselbe Fund wie bei der Auswertung (Runde 11).
  const [error, setError] = useState(false);

  // Laufender Monat — bei jedem Render neu, damit ein über Mitternacht/Monatswechsel
  // offener Tab nicht auf den alten Monat zeigt.
  const now = new Date();
  const currentMonth = now.getMonth() + 1;
  const currentYear  = now.getFullYear();

  const fetchData = async () => {
    setLoading(true);
    setError(false);
    try {
      // /api/content liest aus einer Collection, die in Produktion nicht existiert
      // (derselbe Fund wie bei /api/stats, s.u.) — "Neuester Content" kam deshalb
      // strukturell IMMER leer zurück. GET /api/v1/posts liest wie überall sonst im
      // Produkt aus monthly_plan.
      const [statsRes, contentRes] = await Promise.all([
        axios.get(`${API}/stats`),
        axios.get(`${API}/v1/posts`, { params: { limit: 5 } }),
      ]);
      setStats(statsRes.data);
      setRecentContent(contentRes.data?.items || []);
    } catch (err) {
      setError(true);
      toast.error('Fehler beim Laden der Daten');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-fox-fire" />
      </div>
    );
  }

  return (
    <div data-testid="dashboard-page" className="space-y-8 animate-fade-in">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-3xl font-bold text-stone-900">Übersicht</h1>
          <p className="text-stone-500 mt-1">Willkommen beim Förderfuchs KI Social Media Manager</p>
        </div>
        <NavLink to="/assistent" className="btn-primary flex items-center gap-2">
          <Plus size={18} />
          Neuer Post
        </NavLink>
      </div>

      {/* Autopilot-Status — kommuniziert die Automatik + Pause/Reaktivieren */}
      <AutopilotStatusBanner />

      {/* 1-Klick-Monatsplan — nur solange für DIESEN Monat noch kein Plan existiert.
          Monat/Jahr müssen explizit mitgegeben werden: die Komponente fragt damit
          /api/monthly-plan/status ab und blendet sich bei vorhandenem Plan selbst aus. */}
      <OneClickPlanButton month={currentMonth} year={currentYear} />

      {/* Planfinder-CTA — nur für neue Nutzer ohne aktiven Plan */}
      <PlanfinderBanner />

      {/* Trial-Status — nur wenn ein Plan gewählt wurde */}
      <TrialStatusBanner />

      {/* Go-Live Checkliste / Congrats Banner */}
      <GoLiveChecklist />

      {/* Quick-Action-Tiles */}
      <QuickActionTiles />

      {/* Dashboard Widgets v2 — Status-Kacheln */}
      <DashboardWidgets />

      {/* Letzte Aktivitäten */}
      <RecentActivitiesBox />

      {/* Stats/Plattform/Recent-Content — bei einem Ladefehler NICHT als "0"/"leer"
          zeigen (Fund 24.08.2026): die Kacheln sagen jetzt etwas Echtes, eine 0 wäre
          dann eine Falschaussage über einen Kunden mit echten Entwürfen. Ein
          Ladefehler bekommt deshalb eine eigene, sichtbare Ansicht statt stiller
          Nullen — derselbe Fund wie bei der Auswertung (Runde 11). */}
      {error ? (
        <div className="bg-red-50 border border-red-200 rounded-xl p-6 flex items-center justify-between gap-4">
          <p className="text-sm text-red-700">
            Die Übersichts-Zahlen konnten nicht geladen werden. Das liegt an uns, nicht
            an deinen Beiträgen — deine Daten sind unverändert.
          </p>
          <button
            onClick={fetchData}
            className="flex-shrink-0 px-4 py-2 rounded-lg bg-red-600 text-white text-sm font-medium hover:bg-red-700"
          >
            Erneut laden
          </button>
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <StatCard
              title="Kampagnen"
              value={stats?.total_campaigns || 0}
              icon={Megaphone}
              color="bg-fox-fire"
            />
            <StatCard
              title="Content Entwürfe"
              value={stats?.draft_content || 0}
              icon={FileEdit}
              color="bg-stone-600"
            />
            <StatCard
              title="Geplant"
              value={stats?.scheduled_content || 0}
              icon={Clock}
              color="bg-amber-500"
            />
            <StatCard
              title="Veröffentlicht"
              value={stats?.published_content || 0}
              icon={CheckCircle}
              color="bg-sage-wisdom"
            />
          </div>

          {/* Platform Overview */}
          <div className="bg-white rounded-xl border border-stone-200 p-6">
            <h2 className="font-heading text-xl font-bold mb-4">Content nach Plattform</h2>
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-4">
              {Object.entries(stats?.platforms || {}).map(([platform, count]) => (
                <div key={platform} className="text-center p-4 bg-stone-50 rounded-lg">
                  <div className={`w-10 h-10 mx-auto mb-2 rounded-full flex items-center justify-center ${platformColors[platform]}`}>
                    <PlatformIcon platform={platform} size={18} />
                  </div>
                  <div className="text-2xl font-bold text-stone-900">{count}</div>
                  <div className="text-xs text-stone-500 capitalize">{platform.replace('_', ' ')}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Recent Content */}
          <div className="bg-white rounded-xl border border-stone-200 p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-heading text-xl font-bold">Neuester Content</h2>
              <NavLink to="/monthly-plan" className="text-fox-fire font-medium text-sm hover:underline">
                Alle anzeigen →
              </NavLink>
            </div>
            {recentContent.length === 0 ? (
              <div className="text-center py-8 text-stone-500">
                <FileText className="w-12 h-12 mx-auto mb-2 opacity-50" />
                <p>Noch kein Content erstellt</p>
                <NavLink to="/generator" className="text-fox-fire font-medium hover:underline">
                  Jetzt starten
                </NavLink>
              </div>
            ) : (
              <div className="space-y-3">
                {recentContent.map((item) => (
                  <ContentRow key={item.id} item={item} />
                ))}
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};

const StatCard = ({ title, value, icon: Icon, color }) => (
  <div className="bg-white rounded-xl border border-stone-200 p-6 card-hover">
    <div className="flex items-center justify-between">
      <div>
        <p className="text-stone-500 text-sm">{title}</p>
        <p className="text-3xl font-bold mt-1">{value}</p>
      </div>
      <div className={`w-12 h-12 ${color} rounded-xl flex items-center justify-center`}>
        <Icon className="w-6 h-6 text-white" />
      </div>
    </div>
  </div>
);

export { StatCard, ContentRow };
export default Dashboard;
